export type SubscriptionTier = 'free' | 'standard' | 'premium';
export type SubscriptionStatus = 'active' | 'expired' | 'cancelled' | 'trial';
export type SubscriptionPlatform = 'ios' | 'android' | 'web';

export interface Subscription {
  userId: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  platform: SubscriptionPlatform;
  startDate: string;
  endDate?: string;
  trialEndDate?: string;
  autoRenew: boolean;
  lastBillingDate?: string;
  nextBillingDate?: string;
  productId?: string; // Store product ID for platform-specific handling
  receiptData?: string; // For receipt validation
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionLimits {
  maxGoals: number;
  showAds: boolean;
  canExportData: boolean;
  exportFormats: ('csv' | 'excel' | 'pdf')[];
  dashboardCards: string[];
  features: string[];
}

export const SUBSCRIPTION_LIMITS: Record<SubscriptionTier, SubscriptionLimits> = {
  free: {
    maxGoals: 3,
    showAds: true,
    canExportData: false,
    exportFormats: [],
    dashboardCards: ['totalGoals', 'totalProgress'],
    features: [
      'Basic goal tracking',
      'Progress monitoring',
      'Up to 3 goals',
      'Basic dashboard'
    ]
  },
  standard: {
    maxGoals: 10,
    showAds: false,
    canExportData: true,
    exportFormats: ['csv'],
    dashboardCards: ['totalGoals', 'totalProgress', 'monthlyContribution', 'completionRate', 'debtOverview', 'savingsOverview', 'investmentOverview'],
    features: [
      'Up to 10 goals',
      'Ad-free experience',
      'Enhanced analytics',
      'Goal performance insights',
      'Financial analysis',
      'Success rate tracking',
      'CSV data export',
      'Full dashboard cards',
      'Goal summary insights',
      'Smart recommendations'
    ]
  },
  premium: {
    maxGoals: 25,
    showAds: false,
    canExportData: true,
    exportFormats: ['csv', 'excel', 'pdf'],
    dashboardCards: ['totalGoals', 'totalProgress', 'monthlyContribution', 'completionRate', 'debtOverview', 'savingsOverview', 'investmentOverview', 'priorityBreakdown'],
    features: [
      'Up to 25 goals',
      'All Standard features',
      'Advanced insights',
      'Predictive analytics',
      'Risk assessment',
      'Excel & PDF exports',
      'All dashboard cards',
      'Investment tracking',
      'Priority breakdown',
      'Detailed goal analytics'
    ]
  }
};

export interface SubscriptionProduct {
  id: string;
  tier: SubscriptionTier;
  platform: SubscriptionPlatform;
  price: number;
  currency: string;
  billingPeriod: 'monthly' | 'yearly';
  trialDays?: number;
  description: string;
}

export interface PurchaseResult {
  success: boolean;
  subscription?: Subscription;
  error?: string;
  receipt?: any;
} 